// AAL-86 MyServices Copilot: the HTTP client of the agent service.
//
// The dashboard reaches the service same-origin under `/ai-copilot/` with the user's Keycloak
// bearer token (`$httpFetch` is not used: it prefixes `/api` and reads whole bodies). The run is
// read as server-sent events over `fetch` + ReadableStream, because EventSource cannot send
// headers. When the stream cannot be opened the one-shot endpoint answers the same result.
// Pure module: the token is handed in, nothing here touches the store or Vue.

import type { PopulatedFormContract, Proposal, ProposalOperation } from './form-contract'
import type { WireAttachment } from './copilot-attachments'

export type MessageRole = 'user' | 'assistant' | 'system'
export type ChatMessage = { role: MessageRole, content: string }

export type UserContext = {
	userId: string
	serviceProviderId?: string | null
	clientId: string
	uiLocale: string
	serviceLocale?: string | null
	/** the Client has `contingent-monitor` on (the page shows the Contingent Calendar); the service re-checks it */
	contingentMonitor?: boolean
}

export type ProposeRequest = {
	messages: ChatMessage[]
	contract: PopulatedFormContract
	user: UserContext
	message: string
	/** `system` when the new turn is the page's validation feedback on an applied Proposal */
	messageRole?: 'user' | 'system'
	/** the files of this turn (copilot-attachments.ts `toWire`): at most 5, 5 MB each, names unique */
	attachments?: WireAttachment[]
}

export type TrajectoryStep = {
	tool: string
	kind: 'tool_call' | 'assistant' | 'reasoning' | 'event'
	turn?: number
	status: 'ok' | 'error' | 'refused'
	reason?: string
	arguments?: Record<string, unknown>
	result?: string
	result_size?: number
	result_truncated?: boolean
	latency_ms?: number
	/** a tool call the service published as it started; false once its result is in */
	running?: boolean
	error?: string | null
	note?: string | null
	/** the operations of a proposal call the contract rejected, each with its path and errors (ticket 16) */
	rejections?: StepRejection[]
}

export type StepRejection = { op: string, path: string, errors: string[] }

export type RejectedOperation = { operation: Record<string, unknown>, errors: string[] }

export type ProposeResult = {
	id: string
	proposal: Proposal
	rejected_operations: RejectedOperation[]
	trajectory: { steps: TrajectoryStep[], stop_reason: string, [key: string]: unknown }
	usage: unknown[]
	cost_usd?: number | null
	model: string
	latency_ms: number
}

export type CopilotFeatures = {
	transcribe: boolean
	/** the service's recording cap; the mic stops a recording here by itself */
	transcribe_max_seconds?: number
	store: boolean
	url_fetch: boolean
	attachments?: boolean
	models: string[]
}

/** The per-field review outcome of a run (ticket 09): what the user did with each proposed change */
export type ReviewOutcome = 'accepted' | 'reverted' | 'pending'
export type ReviewReport = { fields: { path: string, outcome: ReviewOutcome }[], saved: boolean }
export type StoredReview = { run_id: string, path: string, outcome: ReviewOutcome, saved: boolean, reviewer_sub?: string | null, created_at?: string, updated_at?: string }
export type ReviewResponse = { run_id: string, reviews: StoredReview[] }

export type CopilotEvent =
	| { type: 'usage', data: Record<string, unknown> }
	| { type: 'step', index: number, step: TrajectoryStep }
	/** one batch of operations the service accepted while the run goes on (ticket 15); `index` is the offset of its first operation in the Proposal */
	| { type: 'proposal_delta', operations: ProposalOperation[], index: number }
	| { type: 'proposal', result: ProposeResult }
	| { type: 'error', message: string }
	| { type: 'done', id: string | null, latencyMs: number }

export type CopilotErrorKind = 'aborted' | 'http' | 'service' | 'network' | 'protocol'

export class CopilotError extends Error {
	kind: CopilotErrorKind
	status?: number
	constructor(kind: CopilotErrorKind, message: string, status?: number) {
		super(message)
		this.name = 'CopilotError'
		this.kind = kind
		this.status = status
	}
}

export type ProposeOptions = {
	token: string
	signal?: AbortSignal
	onEvent?: (event: CopilotEvent) => void
	/** Injection point for tests; defaults to the global fetch */
	fetch?: typeof fetch
	basePath?: string
}

const BASE_PATH = '/ai-copilot'

// ---------------------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------------------

/**
 * Runs one turn: streams `/propose/stream`, forwarding every event to `onEvent`, and resolves
 * with the ProposeResult. Falls back to the one-shot `/propose` when the stream cannot be
 * opened (network failure, server error, missing body); the one-shot result is still reported
 * as `proposal` and `done` events, so callers have one code path.
 */
export async function propose(body: ProposeRequest, options: ProposeOptions): Promise<ProposeResult> {
	const fetchImpl = options.fetch ?? globalThis.fetch
	const base = options.basePath ?? BASE_PATH
	const emit = options.onEvent ?? (() => {})
	const init = (accept: string): RequestInit => ({
		method: 'POST',
		headers: { Authorization: `Bearer ${options.token}`, 'Content-Type': 'application/json', Accept: accept },
		body: JSON.stringify(body),
		signal: options.signal,
	})

	let response: Response | undefined
	try {
		response = await fetchImpl(`${base}/propose/stream`, init('text/event-stream'))
	}
	catch (error) {
		throwIfAborted(error, options.signal)
		// could not open the stream at all: the one-shot route takes over
	}
	if (response && !response.ok && !isRetryableOnOneShot(response.status)) {
		throw new CopilotError('http', await errorDetail(response), response.status)
	}
	if (response?.ok && response.body) return readStream(response.body, emit, options.signal)

	let oneShot: Response
	try {
		oneShot = await fetchImpl(`${base}/propose`, init('application/json'))
	}
	catch (error) {
		throwIfAborted(error, options.signal)
		throw new CopilotError('network', String((error as Error)?.message ?? error))
	}
	if (!oneShot.ok) throw new CopilotError('http', await errorDetail(oneShot), oneShot.status)
	const result = await oneShot.json() as ProposeResult
	emit({ type: 'proposal', result })
	emit({ type: 'done', id: result.id, latencyMs: result.latency_ms })
	return result
}

export async function fetchFeatures(options: Pick<ProposeOptions, 'token' | 'fetch' | 'basePath'>): Promise<CopilotFeatures> {
	const fetchImpl = options.fetch ?? globalThis.fetch
	const response = await fetchImpl(`${options.basePath ?? BASE_PATH}/features`, {
		headers: { Authorization: `Bearer ${options.token}`, Accept: 'application/json' },
	})
	if (!response.ok) throw new CopilotError('http', await errorDetail(response), response.status)
	return await response.json() as CopilotFeatures
}

// the file extension OpenAI reads the container from, per MediaRecorder mime type
const AUDIO_EXTENSIONS: Record<string, string> = {
	'audio/webm': 'webm', 'audio/ogg': 'ogg', 'audio/mp4': 'mp4', 'audio/mpeg': 'mp3', 'audio/wav': 'wav', 'audio/x-m4a': 'm4a',
}

/**
 * Voice input: posts one recording as the `audio` part of a multipart body to `/transcribe` and
 * resolves with the text. The blob keeps its MediaRecorder type (codec parameter included); the
 * browser sets the multipart Content-Type and boundary itself. A failed response rejects with a
 * CopilotError carrying the status (413 too long, 503 off) and the service's detail. `clientId`
 * names the selected Client in the query: the service checks the caller and the Client's
 * `ai-copilot` feature before it reads the audio.
 */
export async function transcribe(audio: Blob, options: Pick<ProposeOptions, 'token' | 'fetch' | 'basePath' | 'signal'> & { clientId: string }): Promise<string> {
	const fetchImpl = options.fetch ?? globalThis.fetch
	const container = (audio.type || 'audio/webm').split(';')[0].trim().toLowerCase()
	const body = new FormData()
	body.append('audio', audio, `dictation.${AUDIO_EXTENSIONS[container] ?? 'webm'}`)
	let response: Response
	try {
		response = await fetchImpl(`${options.basePath ?? BASE_PATH}/transcribe?clientId=${encodeURIComponent(options.clientId)}`, {
			method: 'POST',
			headers: { Authorization: `Bearer ${options.token}`, Accept: 'application/json' },
			body,
			signal: options.signal,
		})
	}
	catch (error) {
		throwIfAborted(error, options.signal)
		throw new CopilotError('network', String((error as Error)?.message ?? error))
	}
	if (!response.ok) throw new CopilotError('http', await errorDetail(response), response.status)
	const result = await response.json() as { text?: string }
	return result.text ?? ''
}

/**
 * Reports the review outcome of one run: `PUT /runs/{id}/review`. The service answers 503 when
 * its store is off (check `fetchFeatures().store` first), 404 for an unknown run and 403 when the
 * run belongs to another user; all surface as a CopilotError of kind `http`.
 */
export async function reportReview(runId: string, report: ReviewReport, options: Pick<ProposeOptions, 'token' | 'fetch' | 'basePath'>): Promise<ReviewResponse> {
	const fetchImpl = options.fetch ?? globalThis.fetch
	const response = await fetchImpl(`${options.basePath ?? BASE_PATH}/runs/${encodeURIComponent(runId)}/review`, {
		method: 'PUT',
		headers: { Authorization: `Bearer ${options.token}`, 'Content-Type': 'application/json', Accept: 'application/json' },
		body: JSON.stringify(report),
	})
	if (!response.ok) throw new CopilotError('http', await errorDetail(response), response.status)
	return await response.json() as ReviewResponse
}

// ---------------------------------------------------------------------------------------
// Server-sent events
// ---------------------------------------------------------------------------------------

export type SseMessage = { event: string, data: string }

/**
 * Incremental parser of the text/event-stream format: feed it chunks, take the complete
 * messages out. Comment lines (`: keep-alive`) are ignored, several `data:` lines are joined
 * with a newline, a blank line dispatches. CRLF and CR line ends are accepted.
 */
export class SseParser {
	private buffer = ''
	private event = ''
	private data: string[] = []

	push(chunk: string): SseMessage[] {
		this.buffer += chunk
		const messages: SseMessage[] = []
		let end: number
		while ((end = this.buffer.search(/\r\n|\r|\n/)) >= 0) {
			const line = this.buffer.slice(0, end)
			this.buffer = this.buffer.slice(end + (this.buffer[end] === '\r' && this.buffer[end + 1] === '\n' ? 2 : 1))
			const message = this.line(line)
			if (message) messages.push(message)
		}
		return messages
	}

	private line(line: string): SseMessage | undefined {
		if (line === '') {
			if (this.data.length === 0) { this.event = ''; return undefined }
			const message = { event: this.event || 'message', data: this.data.join('\n') }
			this.event = ''
			this.data = []
			return message
		}
		if (line.startsWith(':')) return undefined
		const colon = line.indexOf(':')
		const field = colon < 0 ? line : line.slice(0, colon)
		let value = colon < 0 ? '' : line.slice(colon + 1)
		if (value.startsWith(' ')) value = value.slice(1)
		if (field === 'event') this.event = value
		else if (field === 'data') this.data.push(value)
		return undefined
	}
}

async function readStream(body: ReadableStream<Uint8Array>, emit: (event: CopilotEvent) => void, signal?: AbortSignal): Promise<ProposeResult> {
	const reader = body.getReader()
	const decoder = new TextDecoder()
	const parser = new SseParser()
	let result: ProposeResult | undefined
	let failure: string | undefined
	let done = false
	const abort = () => reader.cancel().catch(() => {})
	signal?.addEventListener('abort', abort, { once: true })
	try {
		while (!done) {
			let chunk: ReadableStreamReadResult<Uint8Array>
			try {
				chunk = await reader.read()
			}
			catch (error) {
				throwIfAborted(error, signal)
				throw new CopilotError('network', String((error as Error)?.message ?? error))
			}
			if (signal?.aborted) throw new CopilotError('aborted', 'cancelled')
			if (chunk.done) break
			for (const message of parser.push(decoder.decode(chunk.value, { stream: true }))) {
				const event = toEvent(message)
				if (!event) continue
				emit(event)
				if (event.type === 'proposal') result = event.result
				if (event.type === 'error') failure = event.message
				if (event.type === 'done') done = true
			}
		}
	}
	finally {
		signal?.removeEventListener('abort', abort)
	}
	if (signal?.aborted) throw new CopilotError('aborted', 'cancelled')
	if (result) return result
	if (failure !== undefined) throw new CopilotError('service', failure)
	throw new CopilotError('protocol', 'stream ended without a proposal')
}

function toEvent(message: SseMessage): CopilotEvent | undefined {
	let data: any
	try {
		data = JSON.parse(message.data)
	}
	catch {
		return undefined
	}
	switch (message.event) {
		case 'usage': return { type: 'usage', data }
		case 'step': return { type: 'step', index: data.index, step: data.step }
		case 'proposal_delta': return { type: 'proposal_delta', operations: data.operations ?? [], index: data.index ?? 0 }
		case 'proposal': return { type: 'proposal', result: data }
		case 'error': return { type: 'error', message: data?.message ?? 'unknown error' }
		case 'done': return { type: 'done', id: data?.id ?? null, latencyMs: data?.latency_ms ?? 0 }
		default: return undefined
	}
}

// ---------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------

// Auth and body validation fail the same way on both routes; everything else may be the
// stream route alone (an ingress without the path, a buffering proxy, a crashed worker).
function isRetryableOnOneShot(status: number): boolean {
	return ![ 400, 401, 403, 422 ].includes(status)
}

function throwIfAborted(error: unknown, signal?: AbortSignal): void {
	if (signal?.aborted || (error as Error)?.name === 'AbortError') throw new CopilotError('aborted', 'cancelled')
}

async function errorDetail(response: Response): Promise<string> {
	try {
		const text = await response.text()
		try {
			const json = JSON.parse(text)
			return json?.detail ?? json?.message ?? text
		}
		catch {
			return text || `HTTP ${response.status}`
		}
	}
	catch {
		return `HTTP ${response.status}`
	}
}
